
<div ref="configureTab">
  <panel-block
    v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.inverseLabelable)"
    id="pn-inverse-name"
  >
    <panel-item>
      <label
        :for="selectedObjectModelRelation.idForProp('inverse-name')"
      >Role of Related Object Model(s)</label>
      <expandable-textarea
        :id="selectedObjectModelRelation.idForProp('inverse-name')"
        v-model="selectedObjectModelRelation.inverse_label_title"
        placeholder="Enter relationship name..."
      ></expandable-textarea>
    </panel-item>
  </panel-block>

  <panel-block id="pn-related-object-type" v-if="!isAccountRelationship">
    <panel-item>
      <label for="related-object-type" class="a-select-label">Related to Which Object Models?</label>
      <a-select
        id="related-object-type"
        v-model="selectedObjectModelRelation.related_object_model_ids"
        :options="modelSearchOptions"
        bind-by-array
        multiselect
        @search:finished="addModelsToRepo"
        :selected-option-ids="selectedObjectModelRelation.related_object_model_ids.slice()"
        compress
      ></a-select>
      <pill
        v-for="model in relatedObjectModels"
        :key="model.id"
        :value="model.title"
        full
        :color="relatedModelTagColor"
        @onRemove="tryRemoveRelatedObjectModel(model.id)"
      ></pill>
    </panel-item>
  </panel-block>

  <requireable
    v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.requireable)"
    :model="selectedObjectModelRelation"
  >Require {{selectedObjectModelRelation.labelTitleOr('relationship')}} to have {{selectedObjectModelRelation.inverseLabelTitleOr('an inverse relationship')}}?</requireable>

  <panel-block
    id="pn-delete-agents"
    v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.inverseDependentDestroy)"
  >
    <panel-item>
      <label :for="selectedObjectModelRelation.idForProp('inverse_dependent_destroy')">
        Delete all {{selectedObjectModelRelation.inverseLabelTitleOr('inverse relationships')}} when the {{selectedObjectModelRelation.labelTitleOr('relationship')}} is deleted?
        <property-change-detector
          :left="selectedObjectModelRelation"
          :right="selectedObjectModelRelation.publishedEquivalent"
          :properties="[['inverse_dependent_destroy']]"
        />
      </label>
      <toggle-select
        :id="selectedObjectModelRelation.idForProp('inverse_dependent_destroy')"
        compress
        v-model="selectedObjectModelRelation.inverse_dependent_destroy"
      ></toggle-select>
    </panel-item>
  </panel-block>

  <confirmation-modal
    v-if="modalStateFor(vueModalID.confirmChange).isVisible"
    :state="modalStateFor(vueModalID.confirmChange)"
    :override-config="warningConfig"
    @toggle-modal="handleConfirmation"
  />

  <panel-block id="pn-parent-object-display-properties" v-if="!isAccountRelationship">
    <panel-item>
      <row>
        <column>
          <label
            :for="`related-object-properties-${this.selectedObjectModel.id}`"
            class="a-select-label"
          >Search by Which Indexed Properties for {{selectedObjectModel.title}}?</label>
        </column>
        <suffix>
          <help-tooltip
            text="Choose indexed properties to search & display about the records in the current object model to confirm the user is establishing a relationship with the correct record."
          ></help-tooltip>
        </suffix>
      </row>
      <a-select
        :id="`related-object-properties-${this.selectedObjectModel.id}`"
        :options="displayPropertyOptions(this.selectedObjectModel)"
        :selected-option-ids="selectedParentPropertyIds.slice()"
        bind-by-object
        multiselect
        compress
        @input="onSelectParentDisplayProperty($event)"
        :placeholder="selectedParentPropertyIds.length > 3 ? 'Max of 4 Properties Allowed' : 'Select one...'"
        :disabled="selectedParentPropertyIds.length > 3"
      ></a-select>
      <pill
        v-for="property in selectedParentDisplayProperties(this.selectedObjectModel)"
        :key="selectedPropertyKey(property)"
        :value="selectedPropertyName(property)"
        full
        :color="relatedModelTagColor"
        @onRemove="tryRemoveParentDisplayProperty(property)"
      ></pill>
    </panel-item>
  </panel-block>

  <panel-block
    id="pn-related-object-properties"
    v-for="model in relatedObjectModels"
    :key="model.id"
    v-if="!isAccountRelationship"
  >
    <panel-item>
      <row>
        <column>
          <label
            :for="`related-object-properties-${model.id}-inverse`"
            class="a-select-label">
            Search by Which Indexed Properties for {{model.title}}?
          </label>
        </column>
        <suffix>
          <help-tooltip text="Choose indexed properties to search & display about the related record to confirm the user is establishing a relationship with the correct record."></help-tooltip>
        </suffix>
      </row>
      <a-select
        :id="`related-object-properties-${model.id}-inverse`"
        :options="displayPropertyOptions(model)"
        bind-by-object
        multiselect
        compress
        :selected-option-ids="selectedRelatedPropertyIds(model).slice()"
        @input="onSelectDisplayProperty($event, model)"
        :placeholder="selectedRelatedPropertyIds(model).length > 3 ? 'Max of 4 Properties Allowed' : 'Select one...'"
        :disabled="selectedRelatedPropertyIds(model).length > 3"
      ></a-select>
      <pill
        v-for="property in selectedDisplayProperties(model)"
        :key="selectedPropertyKey(property)"
        :value="selectedPropertyName(property)"
        full
        :color="relatedModelTagColor"
        @onRemove="tryRemoveDisplayProperty(property)"
      ></pill>
    </panel-item>
  </panel-block>

  <div v-if="false"> <!-- disabled until implementation story -->
    <panel-block  v-for="model in relatedObjectModels" :key="model.id">
      <panel-item>
        <label for="related-object-list-view" class="a-select-label">
          Display Which List View for {{model.title}}?
          <help-tooltip text="Choose which list view to display about the related records to confirm the user is establishing a relationship with the correct record. Searching for existing records will use the search bar configured in your list view so ensure the list view has a search bar configured."></help-tooltip>
        </label>

        <a-select
          :id="`related-object-type-${model.id}`"
          :options="listViewOptions(model.id)"
          compress
          placeholder="Search list views"
          @input="value => onSelectListView(model.id, value)"
          ></a-select>

      </panel-item>
    </panel-block>
  </div>

  <template v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.accountRelationUserTypeable)">
    <panel-block id="pn-type-of-user-relationship">
      <panel-item>
        <label for="user-type">
          Type of user relationship?
        </label>
        <a-select
          id="user-type"
          v-model="selectedObjectModelRelation.user_type"
          :options="userTypeOptions"
          compress
          :disabled="!canChangeUserType"
        ></a-select>
      </panel-item>
    </panel-block>

    <panel-block
      id="pn-user-relationship-group-filter"
      v-if="selectedObjectModelRelation.isFormabilityUserType"
    >
      <panel-item>
        <label for="user-type-filters">
          Who can be referenced?
        </label>
        <a-select
          id="user-type-filters"
          :options="userTypeFiltersOptions"
          :selected-option-ids="selectedObjectModelRelation.userTypeFiltersHandler.map(x => x.reference_id)"
          @input="createUserTypeFilter"
          @destroy="destroyUserTypeFilter"
          bind-by-object
          multiselect
          compress
          :placeholder="userTypeFilterPlaceholder"
        ></a-select>
        <scroll-area>
          <ul class="tag-listing">
            <li v-for="filter in selectedObjectModelRelation.user_type_filters">
              <template v-if="filter.reference_type === assignableEnum.group">
                <pill :color="filter.tagColor"
                      @onRemove="filter.destroy()"
                      :value="`${filter.display}`"
                ></pill>
              </template>
            </li>
          </ul>
        </scroll-area>
      </panel-item>
    </panel-block>
  </template>
</div>
